import d3 from "d3";

import { IGrowthChartGraph } from "./GrowthChartGraph";
import { IGrowthChartsCustomAreaHandler } from "./IGrowthChartsCustomAreaHandler";

interface IChartCoordinates {
    x: number;
    y0: number;
    y1: number;
}

class BmiCustomAreaHandler implements IGrowthChartsCustomAreaHandler {
    public applyCustomArea(chartId: string, chart: IGrowthChartGraph, data: c3.Data, xFunction: (x: number) => number, yFunction: (x: number) => number): void {
        const p01x = data.columns.find(x => x.contains("p01_x"));
        const p01y = data.columns.find(x => x.contains("p01_y"));
        const p97x = data.columns.find(x => x.contains("p97_x"));
        const p97y = data.columns.find(x => x.contains("p97_y"));
        const p85x = data.columns.find(x => x.contains("p85_x"));
        const p85y = data.columns.find(x => x.contains("p85_y"));
        const p99x = data.columns.find(x => x.contains("p99_x"));
        const p99y = data.columns.find(x => x.contains("p99_y"));
        const p3y = data.columns.find(x => x.contains("p3_y"));

        this.removeOverlays(chartId);
        this.applyCustomCurves(chartId);

        // under p99 light area
        const p99cordLight = this.generateAreaCoordinates(p99x as number[], p97y as number[], p99y as number[]);
        const areap99Light = this.generateArea(xFunction, yFunction, p99cordLight);
        this.appendArea(chartId, p99cordLight, areap99Light, "area-white");

        // under p85 white area
        const p85cordLight = this.generateAreaCoordinates(p99x as number[], p97y as number[], p85y as number[]);
        const areap85Light = this.generateArea(xFunction, yFunction, p85cordLight);
        this.appendArea(chartId, p85cordLight, areap85Light, "area-full-white");

        // under p01 curve dark area
        const p01cord = this.generateBasicAreaCoordinates(p01x as number[], chart.yAxis.min, p01y as number[]);
        const areap01 = this.generateArea(xFunction, yFunction, p01cord);
        this.appendArea(chartId, p01cord, areap01, "area-dark");

        // over p97 curve dark area
        const p97cord = this.generateBasicAreaCoordinates(p97x as number[], 0, p97y as number[]);
        const areap972 = this.generateBasicArea(xFunction, yFunction, p97cord);
        this.appendArea(chartId, p97cord, areap972, "area-dark");

        // between p97 and p85 curves light  area
        const p85cord = this.generateAreaCoordinates(p85x as number[], p85y as number[], p97y as number[]);
        const areap852 = this.generateArea(xFunction, yFunction, p85cord);
        this.appendArea(chartId, p85cord, areap852, "area-light");

        // between p3 and p01 curves light area
        const p3cord = this.generateAreaCoordinates(p01x as number[], p01y as number[], p3y as number[]);
        const areap32 = this.generateArea(xFunction, yFunction, p3cord);
        this.appendArea(chartId, p3cord, areap32, "area-light");
    }

    private applyCustomCurves(chartId: string): void {
        const p99Curve = d3.select(`#${chartId} .c3-chart-line.c3-target-p99-y path`).node() as SVGGeometryElement;
        const p97Curve = d3.select(`#${chartId} .c3-chart-line.c3-target-p97-y path`).node() as SVGGeometryElement;
        const p85Curve = d3.select(`#${chartId} .c3-chart-line.c3-target-p85-y path`).node() as SVGGeometryElement;
        const p3Curve = d3.select(`#${chartId} .c3-chart-line.c3-target-p3-y path`).node() as SVGGeometryElement;
        const p99LastPoint = p99Curve.getPointAtLength(p99Curve.getTotalLength() - 1);
        const p97StartPoints = [];
        const p97EndingPoints = [];
        const p85EndingPoints = [];
        const p3Ponts = [];
        for (let i = 0; i < p97Curve.getTotalLength(); i++) {
            const point = p97Curve.getPointAtLength(i);
            if (point.x > p99LastPoint.x)
                p97EndingPoints.push(point);
            else
                p97StartPoints.push(point);
        }

        for (let i = 0; i < p85Curve.getTotalLength(); i++) {
            const point = p85Curve.getPointAtLength(i);
            if (point.x > p99LastPoint.x)
                p85EndingPoints.push(point);
        }

        for (let i = 0; i < p3Curve.getTotalLength(); i++) {
            const point = p3Curve.getPointAtLength(i);
            p3Ponts.push(point);
        }

        this.appendCurve(chartId, this.buildPathFromPoints(p97EndingPoints), "c3-curve-dashed-overlay");
        this.appendCurve(chartId, this.buildPathFromPoints(p97StartPoints), "c3-curve-dotted-overlay-light");
        this.appendCurve(chartId, this.buildPathFromPoints(p85EndingPoints), "c3-curve-dotted-overlay-light");
        this.appendCurve(chartId, this.buildPathFromPoints(p3Ponts), "c3-curve-dotted-overlay-light");
    }

    private buildPathFromPoints(points: DOMPoint[]): string {
        const line = d3.svg.line<DOMPoint>()
            .x(d => d.x)
            .y(d => d.y - 1);

        return line(points);
    }

    private removeOverlays(chartId: string): void {
        d3.selectAll(`#${chartId} .c3-curve-dashed-overlay`).remove();
        d3.selectAll(`#${chartId} .c3-curve-dashed-overlay-light`).remove();
        d3.selectAll(`#${chartId} .c3-curve-dotted-overlay-light`).remove();
        d3.selectAll(`#${chartId} .area-white`).remove();
        d3.selectAll(`#${chartId} .area-full-white`).remove();
        d3.selectAll(`#${chartId} .area-light`).remove();
        d3.selectAll(`#${chartId} .area-dark`).remove();
    }

    private generateAreaCoordinates(xPoints: number[], y0Points: number[], y1Points: number[]): IChartCoordinates[] {
        const areaCoordinates: IChartCoordinates[] = [];
        for (let i = 1; i < xPoints.length; i++) {
            const coordinate = {
                x: xPoints[i],
                y0: y1Points[i],
                y1: y0Points[i]
            };
            areaCoordinates.push(coordinate);
        }
        return areaCoordinates;
    }

    private generateBasicAreaCoordinates(xPoints: number[], yPoint: number, y1Points: number[]): IChartCoordinates[] {
        const areaCoordinates: IChartCoordinates[] = [];
        for (let i = 1; i < xPoints.length; i++) {
            const coordinate = {
                x: xPoints[i],
                y0: yPoint,
                y1: y1Points[i]
            };
            areaCoordinates.push(coordinate);
        }
        return areaCoordinates;
    }

    private generateArea(xFunction: (x: number) => number, yFunction: (x: number) => number, coordinates: IChartCoordinates[]): d3.svg.Area<number> {
        return d3.svg.area<number>()
            .interpolate("cardinal")
            .x(d => xFunction(coordinates[d].x))
            .y0(d => yFunction(coordinates[d].y0))
            .y1(d => yFunction(coordinates[d].y1));
    }

    private generateBasicArea(xFunction: (x: number) => number, yFunction: (x: number) => number, coordinates: IChartCoordinates[]): d3.svg.Area<number> {
        return d3.svg.area<number>()
            .interpolate("cardinal")
            .x(d => xFunction(coordinates[d].x))
            .y0(0)
            .y1(d => yFunction(coordinates[d].y1));
    }

    private appendArea(chartId: string, coordinates: IChartCoordinates[], area: d3.svg.Area<number>, className: string): void {
        d3.select(`#${chartId} svg g`)
            .insert("path", ":first-child")
            .datum(d3.range(coordinates.length))
            .attr("class", className)
            .attr("d", area);
    }

    private appendCurve(chartId: string, path: string, className: string): void {
        d3.select(`#${chartId} svg g`)
            .insert("path", ":first-child")
            .attr("class", className)
            .attr("d", path);
    }
}

export { BmiCustomAreaHandler };
